import {
  isValidElement,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type ComponentProps,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api.ts';
import { SearchSelect } from './SearchSelect.tsx';
import { optionsFromChildren, SEARCH_SELECT_MIN_OPTIONS } from './search-select.ts';

function cx(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

export function Button({
  variant = 'primary',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  return (
    <button
      type="button"
      {...props}
      className={cx(
        'loot-control inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'primary' && 'bg-accent text-on-accent shadow-sm hover:opacity-90',
        variant === 'secondary' &&
          'border border-control-border bg-sheet text-ink hover:border-ink',
        variant === 'danger' && 'border border-danger text-danger hover:bg-danger hover:text-sheet',
        className,
      )}
    />
  );
}

export function PageHeader({
  title,
  description,
  meta,
  actions,
}: {
  title: string;
  description?: string;
  /**
   * Ficha em tela completa: a linha logo abaixo do título com o código (em fonte de código) e a
   * situação em `Badge` (ex.: "FAC-2026-000012 · Em execução").
   */
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 border-b border-rule pb-4">
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="max-w-2xl text-sm text-muted">{description}</p>}
        {meta && <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">{meta}</div>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Indicador dos painéis: o número grande, com algarismos de mesma largura, e embaixo o que ele
 * conta. `alert` destaca em vermelho quando há algo a resolver (atrasados, risco); o rótulo diz o
 * quê, a cor só reforça.
 */
export function StatCard({
  label,
  value,
  alert = false,
  detail,
}: {
  label: string;
  /** Contagem (formatada aqui) ou valor já formatado por quem chama (ex.: um total em moeda). */
  value: number | string;
  alert?: boolean;
  /** Linha pequena embaixo do rótulo (ex.: a variação em relação a outra versão). */
  detail?: ReactNode;
}) {
  const { i18n } = useTranslation();
  const warn = alert && typeof value === 'number' && value > 0;
  return (
    <div
      className={cx(
        'flex flex-col gap-0.5 rounded-lg border bg-sheet p-4 shadow-sm',
        warn ? 'border-danger' : 'border-rule',
      )}
    >
      <span className={cx('text-2xl font-semibold tabular-nums', warn && 'text-danger')}>
        {typeof value === 'number' ? value.toLocaleString(i18n.language) : value}
      </span>
      <span className="text-sm text-muted">{label}</span>
      {detail && <span className="text-xs text-muted">{detail}</span>}
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
}: {
  title?: string;
  /** Ação do bloco, à direita do título ("Anexar documento", "Nova obrigação"). */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-lg border border-rule bg-sheet p-5 shadow-sm">
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {title && <h2 className="text-base font-semibold">{title}</h2>}
          {action && <div className="ml-auto flex flex-wrap gap-2">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: ReactNode;
  hint?: string;
  error?: string;
  /** Marca o campo como obrigatório (asterisco; leitores de tela ouvem "obrigatório"). */
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <label className={cx('flex min-w-0 flex-col gap-1 text-sm', className)}>
      <span className="font-medium">
        {label}
        {required && (
          <>
            <span aria-hidden className="ml-0.5 text-danger">
              *
            </span>
            <span className="sr-only"> ({t('common.required')})</span>
          </>
        )}
      </span>
      {children}
      {hint && !error && <span className="text-xs text-muted">{hint}</span>}
      {error && <span className="text-xs text-danger">{error}</span>}
    </label>
  );
}

const inputClass =
  'loot-control min-h-10 w-full rounded-md border border-control-border bg-sheet px-3 py-2 text-sm text-ink focus:border-accent focus:ring-3 focus:ring-accent/20 focus:outline-none focus-visible:outline-none';

export function Input(props: ComponentProps<'input'>) {
  return <input {...props} className={cx(inputClass, props.className)} />;
}

/**
 * Campo de seleção comum. Com muitas opções, também aceita digitar para achar o item (nome, código
 * ou parte deles), além de rolar a lista. Quem usa o campo não muda nada: recebe o mesmo `onChange`.
 */
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  const options = props.multiple ? null : optionsFromChildren(props.children);
  if (!options || props.value === undefined || options.length < SEARCH_SELECT_MIN_OPTIONS) {
    return <select {...props} className={cx(inputClass, 'loot-select', props.className)} />;
  }
  // A primeira opção sem valor ("Escolha", "Todas") é a que limpa o campo.
  const blank = options.find((o) => o.id === '');
  const change = (value: string) =>
    props.onChange?.({
      target: { value },
      currentTarget: { value },
    } as unknown as ChangeEvent<HTMLSelectElement>);
  return (
    <SearchSelect
      value={String(props.value)}
      options={options.filter((o) => o.id !== '')}
      onChange={change}
      noneLabel={blank?.disabled ? undefined : blank?.label}
      placeholder={blank?.label}
      disabled={props.disabled}
      required={props.required}
      id={props.id}
      ariaLabel={props['aria-label']}
      className={props.className}
    />
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'ok' | 'off' | 'info' | 'accent';
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        'inline-block rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        tone === 'neutral' && 'border border-rule text-muted',
        tone === 'ok' && 'bg-ok/15 text-ok',
        tone === 'off' && 'bg-danger/10 text-danger',
        tone === 'info' && 'bg-info/12 text-info',
        tone === 'accent' && 'bg-brand-soft text-brand-text',
      )}
    >
      {children}
    </span>
  );
}

/** Título de coluna: texto ou `{ label, align: 'right' }` para valores e quantidades. */
export type TableHead = ReactNode | { label: ReactNode; align: 'right' };

function isAlignedHead(h: TableHead): h is { label: ReactNode; align: 'right' } {
  return typeof h === 'object' && h !== null && !isValidElement(h) && 'label' in h;
}

export function Table({ head, children }: { head: TableHead[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-rule bg-sheet shadow-sm">
      {/* A largura mínima é do celular: no computador, a tabela acompanha o painel em que está. */}
      <table className="w-full min-w-[640px] border-collapse text-sm lg:min-w-0">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                className={cx(
                  'border-b border-rule bg-sheet px-4 py-2 text-xs font-medium text-muted',
                  isAlignedHead(h) ? 'text-right' : 'text-left',
                )}
              >
                {isAlignedHead(h) ? h.label : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_td]:border-b [&_td]:border-rule [&_td]:px-4 [&_td]:py-2 [&_td]:align-middle [&_tr:hover_td]:bg-paper/60 [&_tr:last-child_td]:border-0">
          {children}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Tabela de valores por mês (Budget, Realizado, comparações): o cabeçalho e a primeira coluna
 * ficam fixos enquanto a tabela rola dentro da própria caixa, com linhas compactas. Linhas de
 * total e subtotal em `TotalRow`; valores em `NumCell`.
 */
export function ValueTable({
  head,
  label,
  children,
}: {
  head: TableHead[];
  /** Nome da tabela para leitores de tela (a caixa rola e recebe o foco do teclado). */
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className="max-h-[70vh] overflow-auto rounded-lg border border-rule bg-sheet shadow-sm"
    >
      <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
        <thead className="[&_th:first-child]:left-0 [&_th:first-child]:z-20 [&_th:first-child]:border-r">
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                className={cx(
                  'sticky top-0 z-10 border-b border-rule bg-sheet px-3 py-2 text-xs font-medium whitespace-nowrap text-muted',
                  isAlignedHead(h) ? 'text-right' : 'text-left',
                )}
              >
                {isAlignedHead(h) ? h.label : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_td]:border-b [&_td]:border-rule [&_td]:px-3 [&_td]:py-2 [&_td]:align-middle [&_td:first-child]:sticky [&_td:first-child]:left-0 [&_td:first-child]:z-[1] [&_td:first-child]:border-r [&_td:first-child]:bg-sheet [&_tr:hover_td]:bg-paper [&_tr:last-child_td]:border-b-0 [&_tr[data-total]_td]:bg-paper [&_tr[data-total]_td]:font-semibold">
          {children}
        </tbody>
      </table>
    </div>
  );
}

/** Linha de total ou subtotal de uma `ValueTable`: letra mais forte e fundo levemente destacado. */
export function TotalRow({ children }: { children: ReactNode }) {
  return <tr data-total="">{children}</tr>;
}

/** Célula de valor ou quantidade: à direita, algarismos de mesma largura, sem quebrar linha. */
export function NumCell({ children }: { children: ReactNode }) {
  return <td className="text-right whitespace-nowrap tabular-nums">{children}</td>;
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-8 text-center text-muted">
        {children}
      </td>
    </tr>
  );
}

/** Mensagem do erro da API para um campo (`path` do zod), para mostrar ao lado dele. */
export function issueFor(error: unknown, path: string): string | undefined {
  return error instanceof ApiError
    ? error.issues.find((i) => i.path === path || i.path.startsWith(`${path}.`))?.message
    : undefined;
}

/** Mostra um erro da API em linguagem simples, com a lista de campos a corrigir. */
/**
 * Erro devolvido pela API. `showPaths={false}` esconde o nome técnico do campo nas pendências
 * (use quando o formulário já mostra cada erro ao lado do campo). Quando o banco explica o motivo
 * (regra sem campo), a mensagem genérica dá lugar ao motivo.
 */
export function ErrorBox({ error, showPaths = true }: { error: unknown; showPaths?: boolean }) {
  const { t } = useTranslation();
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  const reasonOnly =
    (e?.code === 'invalid_value' || e?.code === 'forbidden_by_database') &&
    e.issues.length > 0 &&
    e.issues.every((i) => !i.path);
  const key = reasonOnly ? 'errors.invalid_value_reason' : `errors.${e?.code ?? 'internal_error'}`;
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
    >
      <p>{t(key, { ...e?.details, defaultValue: t('errors.internal_error') })}</p>
      {e?.issues && e.issues.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {e.issues.map((i, idx) => (
            <li key={idx}>
              {showPaths && i.path && <code className="font-mono">{i.path}</code>} {i.message}
            </li>
          ))}
        </ul>
      )}
      {e?.requestId && (
        <p className="mt-1 font-mono text-xs opacity-70">
          {t('errors.reference')}: {e.requestId}
        </p>
      )}
    </div>
  );
}

export function Loading() {
  const { t } = useTranslation();
  return <p className="text-sm text-muted">{t('common.loading')}</p>;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...props} className={cx(inputClass, 'min-h-20', props.className)} />;
}
